import React, { useMemo, useState } from 'react';
import { FlightCardWidget, type FlightCardPayload } from './FlightCardWidget';
import { durationMinutes, priceAmount } from './flightPresentation';
import styles from './FlightContainerWidget.module.scss';

type FlightListPayload = {
  title: string;
  items: FlightCardPayload[];
  return_items?: FlightCardPayload[];
  direction?: string;
  emphasis_offer_id?: string;
  total_count: number;
  currency: string;
};

const TABS = ['Best Value', 'Cheapest', 'Fastest'] as const;
type FlightTab = (typeof TABS)[number];

export function FlightContainerWidget({
  payload,
  onAction,
}: {
  payload: FlightListPayload;
  onAction?: (actionId: string, value?: Record<string, unknown>) => void;
}) {
  const [activeTab, setActiveTab] = useState<FlightTab>('Best Value');
  const [selectedOutboundId, setSelectedOutboundId] = useState<string | null>(null);
  const [selectedReturnId, setSelectedReturnId] = useState<string | null>(null);
  const outboundSource = payload.items || [];
  const inboundSource = payload.return_items || [];
  // Outbound and return are shown together, side by side, whenever the
  // search came back with return flights at all.
  const twoColumns = inboundSource.length > 0;

  const sortItems = (source: FlightCardPayload[]) => {
    const list = [...source];
    if (activeTab === 'Cheapest') {
      return list.sort((a, b) => priceAmount(a.price?.total) - priceAmount(b.price?.total));
    }
    if (activeTab === 'Fastest') {
      /* By parsed minutes, not by the raw ISO string — comparing those as
         text sorts PT9H after PT10H. */
      return list.sort(
        (a, b) =>
          durationMinutes(a.itineraries?.[0]?.duration || a.duration) -
          durationMinutes(b.itineraries?.[0]?.duration || b.duration),
      );
    }
    return list; // Best Value keeps the order the assistant ranked them in
  };
  const outboundItems = useMemo(() => sortItems(outboundSource), [outboundSource, activeTab]);
  const inboundItems = useMemo(() => sortItems(inboundSource), [inboundSource, activeTab]);

  const selectedOutbound = outboundSource.find((item) => item.offer_id === selectedOutboundId) || null;

  // A round trip's details need the chosen outbound alongside the return
  // offer being viewed; Chat.tsx merges the two.
  const handleAction = (actionId: string, value?: Record<string, unknown>) => {
    if (twoColumns && selectedOutbound && actionId.startsWith('view_offer:')) {
      onAction?.(actionId, { ...value, outbound_offer: selectedOutbound });
      return;
    }
    onAction?.(actionId, value);
  };

  return (
    <section className={styles.root} aria-label={payload.title || 'Flights'}>
      <div className={styles.titleRow}>
        <h3 className={styles.title}>{twoColumns ? 'Round trip flights' : payload.title}</h3>
        <span className={styles.chip}>
          {payload.total_count} offers{payload.currency ? ` · ${payload.currency}` : ''}
        </span>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Sort flights">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`${styles.tab}${activeTab === tab ? ` ${styles.tabActive}` : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {twoColumns ? (
        <div className={styles.columns}>
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Outbound flights</h3>
            <div className={styles.list} role="listbox" aria-label="Outbound flights">
              {outboundItems.map((item) => (
                <FlightCardWidget
                  key={item.offer_id}
                  payload={{
                    ...item,
                    ai_adjustments:
                      item.offer_id === payload.emphasis_offer_id
                        ? { emphasis: 'cheapest' }
                        : item.ai_adjustments,
                  }}
                  selected={item.offer_id === selectedOutboundId}
                  onSelect={() => setSelectedOutboundId(item.offer_id)}
                  showView={false}
                />
              ))}
            </div>
          </div>

          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Return flights</h3>
            <div className={styles.list} role="listbox" aria-label="Return flights">
              {inboundItems.map((item) => (
                <FlightCardWidget
                  key={item.offer_id}
                  payload={item}
                  selected={item.offer_id === selectedReturnId}
                  onSelect={() => setSelectedReturnId(item.offer_id)}
                  onAction={handleAction}
                  viewDisabled={!selectedOutbound}
                  viewDisabledReason="Select an outbound flight first"
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.list} role="listbox" aria-label="Flights">
          {outboundItems.map((item) => (
            <FlightCardWidget
              key={item.offer_id}
              payload={{
                ...item,
                ai_adjustments:
                  item.offer_id === payload.emphasis_offer_id
                    ? { emphasis: 'cheapest' }
                    : item.ai_adjustments,
              }}
              selected={item.offer_id === selectedOutboundId}
              onSelect={() => setSelectedOutboundId(item.offer_id)}
              onAction={handleAction}
            />
          ))}
        </div>
      )}
    </section>
  );
}
